import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import orgamax from 'ampersand-app'

import lang from '@/lang'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { awaitableConfirmationModal } from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'

import { priceKinds, RECEIPT_TYPES, SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import SquareIconButton, { VARIANTS } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import TaxCategoryDrawer from '@/modules/base/components/tax-categories/tax-categories-drawer'
import PositionWithTaxCategoryItem from './position-with-tax-category-item'
import PositionDescriptionEdit from './position-description-edit'
import getCurrentVatRate from './_helpers/getCurrentVatRate'
import { isPositionValueXmlLocked, getPositionFieldValidation } from '@/modules/document/components/review/field-validation'
import { openDrawer, closeDrawer, setSelectedPosition } from '@/redux/ducks/taxCategories/main'
import TrashcanIconSvg from '@assets/images/icons/trashcan.svg'
import { BookingService } from '@/modules/banking/services/index'

// FLAG:useDocuAi — copy for this list's review view (filter, quick selection, position types).
const positionsLang = lang.documents.reviewPositions

const useStyles = makeStyles(theme => ({
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  action: {
    position: 'relative',
    marginLeft: 8,
  },
  actionBtn: {
    minHeight: 46,
    maxHeight: 46,
    minWidth: 46,
    maxWidth: 46,
  },
  // FLAG:useDocuAi — same height as the compact review row so its border lines up.
  reviewActionBtn: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: 'none',
    minHeight: 34,
    maxHeight: 34,
    '& svg': {
      fill: theme.primary,
    },
  },
  // FLAG:useDocuAi — review position tools (multi-select bulk category assign)
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.75),
  },
  chipLabel: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
  },
  chip: {
    fontSize: theme.fontSizes.smaller,
    color: theme.primary,
    background: 'transparent',
    border: `1px solid ${theme.borderColor}`,
    borderRadius: theme.borderRadiusPill,
    padding: '3px 12px',
    cursor: 'pointer',
  },
  reset: {
    marginLeft: 'auto',
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  reviewItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: '6px 10px',
    marginBottom: 6,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: theme.borderRadiusLarge,
    background: theme.white,
  },
  reviewItemSelected: {
    borderColor: theme.primary,
    background: theme.primaryBrightBackground,
  },
  checkbox: {
    marginTop: 2,
    flex: '0 0 auto',
    cursor: 'pointer',
  },
  reviewBody: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  reviewDesc: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
    color: theme.textColor,
    marginBottom: 4,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  reviewDescEmpty: {
    color: theme.textLight,
    fontWeight: theme.fontWeights.regular,
    fontStyle: 'italic',
  },
  // FLAG:useDocuAi — the graded per-line verdict from fieldMeta.positions[i], same rule
  // as everywhere else: 'error' red, 'warning' the yellow review colour, 'ok'/'info' nothing.
  reviewDescError: {
    color: theme.red,
    fontWeight: 600,
  },
  reviewDescWarning: {
    color: theme.colors?.yellow || theme.yellow,
    fontWeight: 600,
  },
  // FLAG:useDocuAi — deposit/credit badge (v1.3.0 Position.kind). Deposit = container deposit
  // (own VAT rate, own booking line); credit = credit/refund.
  kindBadge: {
    display: 'inline-block',
    marginRight: 6,
    padding: '0 7px',
    fontSize: theme.fontSizes.tooltip,
    fontWeight: theme.fontWeights.semiBold,
    lineHeight: '17px',
    borderRadius: theme.borderRadiusPill,
    verticalAlign: 'middle',
    cursor: 'help',
    whiteSpace: 'nowrap',
  },
  kindBadgeDeposit: {
    color: theme.orange,
    border: `1px solid ${theme.orange}`,
  },
  kindBadgeCredit: {
    color: theme.textLight,
    border: `1px solid ${theme.borderColor}`,
  },
  reviewControls: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    // The shared position row is flex:1 0 0 (shrink 0) — it can't compress to the
    // narrow review drawer and overflows the card. Let it shrink here.
    '& > div': {
      minWidth: 0,
      flexShrink: 1,
    },
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding: '10px 12px',
    border: `1px solid ${theme.primary}`,
    borderRadius: theme.borderRadiusLarge,
    background: theme.white,
    // Sits directly above the position list now — the gap belongs below it.
    marginBottom: theme.spacing(1.5),
  },
  bulkCount: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.medium,
    whiteSpace: 'nowrap',
  },
  bulkAssign: {
    fontSize: theme.fontSizes.small,
    color: theme.primary,
    background: 'transparent',
    border: `1px solid ${theme.primary}`,
    borderRadius: theme.borderRadius,
    padding: '6px 14px',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  bulkClear: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
    cursor: 'pointer',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
  },
  filterInput: {
    flex: '1 1 auto',
    minWidth: 0,
    height: 34,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: 6,
    padding: '0 10px',
    fontSize: theme.fontSizes.small,
    background: theme.white,
    color: theme.textColor,
  },
  filterClear: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterEmpty: {
    listStyle: 'none',
    fontSize: theme.fontSizes.small,
    color: theme.textLight,
    padding: '8px 2px',
  },
}))

export const PositionWithTaxCategoryList = (props) => {
  const dispatch = useDispatch()
  const {
    type,
    state,
    receiptId,
    accountType = 'expenditures,incomeExpenditures',
    disabled,
    priceKind = priceKinds.GROSS,
    disableTax,
    disablePrice,
    hideDeleteButton,
    positions,
    onChangePosition,
    onDeletePosition,
    dateFor,
    isNewBookkeepingRecord,
    menuPlacement = 'bottom',
    isEconomicAsset,
    filledData = [],
    ocrDataChanged,
    // FLAG:useDocuAi — invoice-review only: multi-select + bulk category assign.
    // When false the component renders exactly as before (legacy expense/revenue).
    reviewTools = false,
    onBulkChangePositions,
    onResetPositions,
    // FLAG:useDocuAi — identity under which the „Zurücksetzen" snapshot of the line items is kept
    // (see below). The review hosts pass their documentReviewFileId through; without the prop it
    // remains `receiptId`, exactly matching legacy behaviour.
    snapshotKey,
    // FLAG:useDocuAi — XML e-invoice: values are authoritative, so lock VAT + price
    // and let only the category be assigned. lockValues is the whole-doc signal;
    // positionsFieldMeta (documentInvoiceDetails.fieldMeta.positions, aligned 1:1
    // with the rows) additionally locks single XML-sourced lines.
    lockValues = false,
    positionsFieldMeta,
    // FLAG:useDocuAi — receipt currency code for each line-item amount. Default „€" keeps the legacy
    // expense form unchanged (it never had another currency).
    currencySymbol = '€',
    // FLAG:useDocuAi — show VAT AMOUNT per row. It has long been calculated, stored, and booked, but
    // had no column: the row showed only the RATE. Review-only.
    showVatAmount = false,
    // FLAG:useDocuAi — foreign currency: no tax category can be selected. Selection triggers the
    // booking, and bookingApi supports only euros.
    lockCategoryForeignCurrency = false,
    // FLAG:useDocuAi — a second reason why the booking cannot be represented: a VAT rate unknown to
    // bookingApi (foreign VAT, or a rate that did not exist in Germany on the receipt date). A
    // separate reason rather than another foreign-currency flag lets the hint say WHAT is blocked.
    // The text arrives ready-made from the review form because that is where the rates live
    // (invoiceFacts.vatJurisdiction).
    lockCategoryUnbookableVat = false,
    unbookableVatHint,
  } = props

  // Both blocks have the same effect (no tax category = no booking) and must therefore apply at BOTH
  // entry points — row selection AND bulk assignment. With flag-OFF, lockCategoryUnbookableVat is
  // always false, so both remain exactly the foreign-currency expression.
  const lockCategoryForBooking = lockCategoryForeignCurrency || lockCategoryUnbookableVat
  const categoryLockHint = lockCategoryForeignCurrency
    ? positionsLang.foreignCurrencyCategoryHint
    : (lockCategoryUnbookableVat ? unbookableVatHint : undefined)

  const [categories, setCategories] = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [filterText, setFilterText] = useState('')
  // FLAG:useDocuAi — the shared TaxCategoryDrawer memoises its item onClick on
  // [activeCategory], so it can hold a STALE handleTaxSubmit closure across opens.
  // Read the pending bulk assignment (indices) + the live positions / change
  // handler from refs so a bulk click works regardless of that staleness.
  const pendingBulkRef = useRef(null)
  const positionsRef = useRef(positions)
  positionsRef.current = positions
  const onBulkChangeRef = useRef(onBulkChangePositions)
  onBulkChangeRef.current = onBulkChangePositions
  // FLAG:useDocuAi — snapshot the positions as they were when the review opened,
  // so "Zurücksetzen" can restore exactly those (positions only). Re-captured per
  // document, because the review drawer keeps this component mounted while paging
  // between documents — a plain `if (!initialPositionsRef.current)` would therefore be wrong.
  //
  // The key MUST be an identity that exists in the REVIEW. It used to be `receiptId` (= `data.id`),
  // but the review data from buildReviewData has no `id`: both sides of the comparison were
  // `undefined`, and `undefined !== undefined` is false — the snapshot was never taken and the chip
  // rendered on NONE of the three review surfaces. Ironically, it worked only where it was not built
  // for (booked expense, `positionReviewTools`, where `data.id` exists). It is now `snapshotKey` —
  // the documentReviewFileId carried by all three
  // review hosts — with `receiptId` as the fallback for
  // the expense/revenue form.
  //
  // `snapshotTakenRef` differentiates “not yet captured” from “captured under the `undefined`
  // key”. Without that, every host missing both identities (new expense without an ID) would again
  // have no chip, for exactly the same reason.
  //
  // Review mode only: the snapshot is read exclusively
  // by the chip and handleResetPositions, both
  // of which sit behind the legacy return below. Thus nothing happens here with flag-OFF.
  //
  // Host requirement: identity and line items must change in the SAME render. If the new key arrived
  // one render before the new line items, the snapshot would capture the PREVIOUS document's rows
  // under the new identity. The review drawer satisfies this by setting `reviewData` to null in its
  // load effect and switching the form column to the
  // progress indicator (= unmounting this component).
  const positionsSnapshotKey = snapshotKey ?? receiptId
  const initialPositionsRef = useRef()
  const snapshotKeyRef = useRef()
  const snapshotTakenRef = useRef(false)
  if (reviewTools && positions?.length && (!snapshotTakenRef.current || snapshotKeyRef.current !== positionsSnapshotKey)) {
    snapshotTakenRef.current = true
    snapshotKeyRef.current = positionsSnapshotKey
    initialPositionsRef.current = positions.map(position => ({ ...position }))
  }
  const { selectedPosition, open: taxDrawerOpen } = useSelector(state => state.taxCategories.main)
  // FLAG:useDocuAi — drop any pending bulk assignment once the drawer closes, so a
  // later single (inline) category pick is never mistaken for a bulk apply.
  useEffect(() => {
    if (!taxDrawerOpen) pendingBulkRef.current = null
  }, [taxDrawerOpen])
  const classes = useStyles(props)
  const vatList = orgamax.user.getVatList(dateFor)
  // ⚠ `useSelector` used to be INSIDE `if (type === EXPENSE)` here — a conditional hook. This worked
  // while `type` remained constant for a mounted component; since the review now follows the ledger
  // of the selected contact role (review-target.ts), `type` changes MID-LIFETIME from expense to
  // revenue. The hook count then changes between two renders, React compares the next hook's
  // dependencies with `undefined`, and crashes with
  // „Cannot read properties of undefined (reading 'length')" in `areHookInputsEqual`.
  // The selector now runs unconditionally (it is pure and cheap), while evaluation remains
  // conditional. It is read optionally because the selector now ALSO runs on revenue receipts:
  // there (and in tests that render the component without a subscription slice), the details do not exist.
  const subscriptionDetails = useSelector(state => state?.subscription?.details)
  let hasReducedFeatures = false
  if (type === RECEIPT_TYPES.EXPENSE) {
    const expenseModule = (subscriptionDetails?.activeDetails || []).find(item => item.name.toUpperCase() === SUBSCRIPTION_MODULES.EXPENSES && item.used > 0)
    hasReducedFeatures = !!expenseModule?.limitations?.hasReducedFeatures
  }

  useEffect(() => {
    let isUnmounted = false
    const loadCategories = async() => {
      const newCategories = await positions.map(async pos => {
        const result = {}
        if (pos.bookkeepingAccountId) {
          try {
            const account = await BookingService.chartOfAccounts.get(pos.bookkeepingAccountId)
            result.allowedVatRates = account.allowedVatRates
          } catch (e) {}
        }
        return result
      })
      !isUnmounted && setCategories(newCategories)
    }

    loadCategories()

    return () => {
      isUnmounted = true
    }
  }, [])

  const handleDeletePosition = (index) => {
    const categoriesClone = [...categories]
    categoriesClone.splice(index, 1)
    setCategories(categoriesClone)
    setSelected(new Set())
    onDeletePosition(index)
  }

  const renderAction = (index, compact) => {
    const actionButton = (
      <SquareIconButton
        customClass={compact ? classes.reviewActionBtn : classes.actionBtn}
        variant={compact ? undefined : VARIANTS.white}
        icon={TrashcanIconSvg}
        disabled={disabled}
        dataQsId='positionWithTaxCategoryList-buttonDelete'
        onClick={() => { handleDeletePosition(index) }}
      />
    )
    return (
      <div className={classes.action}>
        {actionButton}
      </div>
    )
  }

  const buildAssignedPosition = (position, account) => {
    const { id, accountDescriptionCustom, allowedVatRates, vatOption, accountNo, accountCategory } = account
    return {
      ...position,
      bookkeepingAccountId: id,
      accountVatOption: vatOption,
      bookkeepingAccountDescriptionCustom: accountDescriptionCustom,
      // FLAG:useDocuAi (reviewTools) — the review path keeps a legitimate 0% current rate
      // (`??`) and tolerates a category without allowedVatRates (`?.`); the legacy path stays
      // byte-identical to f2ac6a682f (`||` + allowedVatRates.default).
      vatPercent: reviewTools
        ? (getCurrentVatRate(allowedVatRates, vatList) ?? allowedVatRates?.default)
        : (getCurrentVatRate(allowedVatRates, vatList) || allowedVatRates.default),
      bookkeepingAccountNo: accountNo,
      accountCategory,
      // FLAG:useDocuAi (reviewTools) — base handleTaxSubmit set no isChanged on a drawer
      // category-assign; only the review path marks the line changed, so the legacy output
      // object is identical to base.
      ...(reviewTools ? { isChanged: true } : {}),
    }
  }

  // FLAG:useDocuAi — bulk assign KEEPS each position's own VAT rate. A bulk
  // category change must never rewrite a line's tax rate (e.g. a 7%-default
  // category stamping over a 19% position), so we set the account fields but
  // leave vatPercent (and allowedVatRates) untouched.
  const buildBulkAssignedPosition = (position, account) => {
    const { id, accountDescriptionCustom, vatOption, accountNo, accountCategory } = account
    return {
      ...position,
      bookkeepingAccountId: id,
      accountVatOption: vatOption,
      bookkeepingAccountDescriptionCustom: accountDescriptionCustom,
      bookkeepingAccountNo: accountNo,
      accountCategory,
      isChanged: true,
    }
  }

  const handleTaxSubmit = async(account) => {
    // FLAG:useDocuAi — bulk apply the chosen category to every selected position.
    // Read via refs (see pendingBulkRef note) so a possibly-stale drawer closure
    // still gets the live selection, positions and change handler.
    const bulkIndices = pendingBulkRef.current
    if (bulkIndices && bulkIndices.length) {
      const currentPositions = positionsRef.current || []
      const indices = bulkIndices.filter(index => currentPositions[index])
      const updates = indices.map(index => ({ index, position: buildBulkAssignedPosition(currentPositions[index], account) }))
      onBulkChangeRef.current?.(updates)
      // Transparency: the kept rate may not be one the category normally supports.
      const allowed = account.allowedVatRates?.values
      const keptButUnsupported = indices.filter(index => {
        const rate = currentPositions[index].vatPercent
        return rate !== null && rate !== undefined && allowed && !allowed.includes(rate)
      })
      if (keptButUnsupported.length) {
        toast.showToast({
          type: 'info',
          message: positionsLang.keptVatRate(keptButUnsupported.length),
        })
      }
      pendingBulkRef.current = null
      setSelected(new Set())
      dispatch(closeDrawer())
      dispatch(setSelectedPosition(null))
      return
    }

    const { index, ...positionProps } = selectedPosition
    // FLAG:useDocuAi — §4.2 (review only): a category pick must never silently
    // rewrite the line's VAT. XML-locked lines always keep their rate; otherwise
    // a rate change needs an explicit confirm — declining assigns only the
    // category via the VAT-preserving builder. Legacy keeps the old behavior.
    let buildPosition = buildAssignedPosition
    if (reviewTools) {
      const currentRate = positionProps.vatPercent
      const accountRate = getCurrentVatRate(account.allowedVatRates, vatList) ?? account.allowedVatRates?.default
      const rateWouldChange = currentRate !== null && currentRate !== undefined && accountRate !== currentRate
      if (rateWouldChange) {
        let keepRate = accountRate === null || accountRate === undefined
          || isPositionValueXmlLocked(positionsFieldMeta, index, 'vatPercent', lockValues)
        if (!keepRate) {
          keepRate = !(await awaitableConfirmationModal({
            isOpen: true,
            title: lang.documents.vatChangeConfirm.title,
            description: lang.documents.vatChangeConfirm.description(currentRate, accountRate),
          }))
        }
        if (keepRate) { buildPosition = buildBulkAssignedPosition }
      }
    }
    const updatedPosition = buildPosition(positionProps, account)
    const categoriesClone = [...categories]
    categoriesClone[index] = { allowedVatRates: account.allowedVatRates }
    setCategories(categoriesClone)
    onChangePosition(updatedPosition, index)
    dispatch(closeDrawer())
    dispatch(setSelectedPosition(null))
  }

  const renderPositionItem = (position, index, overrides = {}) => (
    <PositionWithTaxCategoryItem
      disableTaxCategorySelection={hasReducedFeatures || lockCategoryForBooking}
      taxCategoryDisabledHint={lockCategoryForBooking && !hasReducedFeatures
        ? categoryLockHint
        : undefined}
      currencySymbol={currencySymbol}
      index={index}
      receiptId={receiptId}
      state={state}
      disableTax={disableTax}
      disablePrice={disablePrice}
      disabled={disabled}
      position={position}
      category={categories[index]}
      onChangePosition={position => onChangePosition(position, index)}
      filledData={filledData}
      ocrDataChanged={ocrDataChanged}
      dateFor={dateFor}
      isNewBookkeepingRecord={isNewBookkeepingRecord}
      priceKind={priceKind}
      menuPlacement={menuPlacement}
      isEconomicAsset={isEconomicAsset}
      accountType={accountType}
      {...overrides}
    />
  )

  const positionComponents = positions.map((position, index) => {
    return (
      <li className={classes.item} key={`position-${index}`}>
        {renderPositionItem(position, index)}
        {!hideDeleteButton && renderAction(index)}
      </li>
    )
  })

  // FLAG:useDocuAi — legacy expense/revenue render. Everything below (multi-select, filter,
  // VAT chip groups, review rows) is invoice-review-only; return here so none of those
  // computations or React elements are produced on the flag-OFF path (byte-identical to
  // f2ac6a682f). No hooks run past this point, so the early return is React-safe.
  if (!reviewTools) {
    return (
      <>
        <ul>
          {positionComponents}
          <TaxCategoryDrawer onSubmit={handleTaxSubmit} accountType={accountType} />
        </ul>
      </>
    )
  }

  // FLAG:useDocuAi — invoice-review rendering: description headline + multi-select.
  const toggleSelect = (index) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(index)) { next.delete(index) } else { next.add(index) }
      return next
    })
  }
  const selectIndices = (indices) => setSelected(new Set(indices))
  const clearSelection = () => setSelected(new Set())
  const openBulkCategoryDrawer = () => {
    // Snapshot the selection now; the drawer covers the list so it can't change
    // while open, and the ref survives any stale drawer closure. Use Array.from —
    // spreading the Set ([...selected]) miscompiles to a single bad element in this
    // build, which silently made the bulk update a no-op.
    // Full write-up: docs/babel-loose-spread-hazard.md.
    pendingBulkRef.current = Array.from(selected).sort((a, b) => a - b)
    dispatch(openDrawer())
  }
  const handleResetPositions = () => {
    if (!initialPositionsRef.current) return
    onResetPositions?.(initialPositionsRef.current)
    clearSelection()
  }
  // FLAG:useDocuAi — filter the visible positions by article name; selection,
  // quick-select and bulk assign all operate on the currently-visible set.
  const filterQuery = filterText.trim().toLowerCase()
  const matchesFilter = position => !filterQuery || (position.description || '').toLowerCase().includes(filterQuery)
  const visibleIndices = positions.reduce((acc, position, index) => (matchesFilter(position) ? [...acc, index] : acc), [])

  // Group the VISIBLE positions by their effective VAT rate (fall back to the
  // current rate when a line carries none) in ONE pass, so each chip's count matches.
  const currentVatRate = vatList.find(vat => vat.isCurrent)?.value
  const vatGroups = visibleIndices.reduce((groups, index) => {
    const rate = positions[index].vatPercent ?? currentVatRate
    if (rate === null || rate === undefined) return groups
    groups[rate] = groups[rate] || []
    groups[rate].push(index)
    return groups
  }, {})
  const allVisibleSelected = visibleIndices.length > 0 && visibleIndices.every(index => selected.has(index))

  const reviewComponents = (
    <>
      <div className={classes.filterRow}>
        <input
          className={classes.filterInput}
          type='text'
          value={filterText}
          placeholder={positionsLang.filterPlaceholder}
          onChange={event => setFilterText(event.target.value)}
        />
        {filterText && (
          <span className={classes.filterClear} onClick={() => setFilterText('')}>{positionsLang.filterClear}</span>
        )}
      </div>
      <div className={classes.chips}>
        <span className={classes.chipLabel}>{positionsLang.quickSelect}</span>
        <span
          className={classes.chip}
          onClick={() => (allVisibleSelected ? clearSelection() : selectIndices(visibleIndices))}
        >
          {allVisibleSelected ? positionsLang.clearSelection : positionsLang.selectAll(visibleIndices.length)}
        </span>
        {Object.entries(vatGroups).map(([rate, indices]) => (
          <span key={`vat-${rate}`} className={classes.chip} onClick={() => selectIndices(indices)}>
            {rate}% · {indices.length}
          </span>
        ))}
        {onResetPositions && initialPositionsRef.current && (
          <span className={classes.reset} onClick={handleResetPositions}>{positionsLang.reset}</span>
        )}
      </div>
      {/* FLAG:useDocuAi — bulk-assign bar ABOVE the list so it is reachable without
          scrolling past a long position list (only shown once something is selected). */}
      {selected.size > 0 && (
        <div className={classes.bulkBar}>
          <span className={classes.bulkCount}>{positionsLang.selectedCount(selected.size)}</span>
          {/* FLAG:useDocuAi — bulk assignment is the SECOND path to the tax category. If it remained
              open for foreign currency, locking row selection would be purely cosmetic. */}
          <button
            type='button'
            className={classes.bulkAssign}
            onClick={openBulkCategoryDrawer}
            disabled={disabled || lockCategoryForBooking}
            title={lockCategoryForBooking ? categoryLockHint : undefined}
          >
            {positionsLang.assignCategory}
          </button>
          <span className={classes.bulkClear} onClick={clearSelection}>{positionsLang.clearSelection}</span>
        </div>
      )}
      <ul>
        {positions.map((position, index) => {
          // FLAG:useDocuAi — filtering HIDES rather than unmounting: every row triggers the chart-of-
          // accounts fetch on mount (position-with-tax-category-item, effect with dependencies []),
          // so a backspace in the filter generated a ~150 KB request per keystroke — and the in-flight
          // coalescer deliberately does not catch this (no cache). display:none keeps the row mounted
          // (no new fetch, local state preserved); exactly the visibleIndices set used by selection,
          // chips, and empty state remains visible.
          const hiddenByFilter = !matchesFilter(position)
          const vatXmlLocked = isPositionValueXmlLocked(positionsFieldMeta, index, 'vatPercent', lockValues)
          const priceXmlLocked = isPositionValueXmlLocked(positionsFieldMeta, index, 'price', lockValues)
          // FLAG:useDocuAi — the description is XML-locked too (the server keeps its own
          // value on save, see xmlLockedFields), so a locked line must not offer the edit.
          const descXmlLocked = isPositionValueXmlLocked(positionsFieldMeta, index, 'description', lockValues)
          // FLAG:useDocuAi — per-line check colours, same graded rule as the header fields.
          const vatCheck = getPositionFieldValidation(positionsFieldMeta, index, 'vatPercent')
          const priceCheck = getPositionFieldValidation(positionsFieldMeta, index, 'price')
          const descCheck = getPositionFieldValidation(positionsFieldMeta, index, 'description')
          return (
            <li
              className={clsx(classes.reviewItem, selected.has(index) && classes.reviewItemSelected)}
              style={hiddenByFilter ? { display: 'none' } : undefined}
              key={`review-position-${index}`}
            >
              <input
                type='checkbox'
                className={classes.checkbox}
                checked={selected.has(index)}
                disabled={disabled}
                onChange={() => toggleSelect(index)}
                aria-label={positionsLang.selectPosition(index + 1)}
              />
              <div className={classes.reviewBody}>
                <div
                  className={clsx(
                    classes.reviewDesc,
                    !position.description && classes.reviewDescEmpty,
                    descCheck.treatment === 'error' && classes.reviewDescError,
                    descCheck.treatment === 'warning' && classes.reviewDescWarning,
                  )}
                  data-check-state={descCheck.treatment || undefined}
                  title={descCheck.treatment ? (descCheck.note || undefined) : undefined}
                >
                  {position.kind && position.kind !== 'good' && (() => {
                    const { kind, kindReason } = position
                    const isDeposit = kind === 'deposit'
                    const reason = kindReason ? ` (${kindReason})` : ''
                    return (
                      <span
                        className={clsx(classes.kindBadge, isDeposit ? classes.kindBadgeDeposit : classes.kindBadgeCredit)}
                        title={isDeposit
                          ? positionsLang.kind.depositHint(reason)
                          : positionsLang.kind.creditHint(reason)}
                      >
                        {isDeposit ? positionsLang.kind.deposit : positionsLang.kind.credit}
                      </span>
                    )
                  })()}
                  {/* FLAG:useDocuAi — click-to-edit line description: the reviewer can
                      correct a wrong/truncated item name in place; the edit rides on the
                      position and is persisted by the normal review save. */}
                  <PositionDescriptionEdit
                    value={position.description}
                    placeholder={positionsLang.positionPlaceholder(index + 1)}
                    readOnly={descXmlLocked}
                    disabled={disabled}
                    title={descXmlLocked
                      ? lang.documents.xmlLockedValueHint
                      : (descCheck.treatment ? undefined : positionsLang.editDescription)}
                    ariaLabel={positionsLang.editDescriptionAria(index + 1)}
                    dataQsId='positionWithTaxCategoryList-description'
                    onChange={description => onChangePosition({ ...position, description, isChanged: true }, index)}
                  />
                </div>
                <div className={classes.reviewControls}>
                  {renderPositionItem(position, index, {
                    compact: true,
                    // Only the review row gets the VAT column — the legacy row above continues to
                    // render category/rate/amount unchanged.
                    showVatAmount,
                    // FLAG:useDocuAi — the line amount is editable in the review; only an XML
                    // e-invoice locks it, because there the document states the line and our value
                    // would contradict it (priceXmlLocked, per line or whole-document via
                    // `lockValues`). The blanket review lock of 2026-09-16 was lifted by Ralf on
                    // 2026-09-21: it also caught every native/OCR analysis, where the reviewer is
                    // precisely the one who has to correct a misread amount.
                    disablePrice: priceXmlLocked || disablePrice,
                    disableVat: vatXmlLocked,
                    vatXmlLocked,
                    priceXmlLocked,
                    vatCheckState: vatCheck.treatment,
                    priceCheckState: priceCheck.treatment,
                    vatCheckNote: vatCheck.note,
                    priceCheckNote: priceCheck.note,
                  })}
                  {!hideDeleteButton && renderAction(index, true)}
                </div>
              </div>
            </li>
          )
        })}
        {visibleIndices.length === 0 && (
          <li className={classes.filterEmpty}>
            {/* Two different empty states: nothing MATCHES vs nothing IS there. The review no
                longer invents a placeholder row, so "no positions at all" is a real state now. */}
            {positions.length
              ? positionsLang.noMatch(filterText)
              : positionsLang.empty}
          </li>
        )}
      </ul>
    </>
  )

  // reviewTools is always true here (the legacy path returned above).
  return (
    <>
      {reviewComponents}
      <TaxCategoryDrawer onSubmit={handleTaxSubmit} accountType={accountType} />
    </>
  )
}

PositionWithTaxCategoryList.propTypes = {
  type: PropTypes.string,
  receiptId: PropTypes.string,
  accountType: PropTypes.string,
  state: PropTypes.string,
  disabled: PropTypes.bool,
  priceKind: PropTypes.string,
  priceTotal: PropTypes.number,
  disableTax: PropTypes.bool,
  disablePrice: PropTypes.bool,
  hideDeleteButton: PropTypes.bool,
  positions: PropTypes.array,
  onChangePosition: PropTypes.func,
  onDeletePosition: PropTypes.func,
  menuPlacement: PropTypes.oneOf(['top', 'bottom', 'auto']),
  dateFor: PropTypes.string,
  isNewBookkeepingRecord: PropTypes.bool,
  showFillAnimation: PropTypes.bool,
  isEconomicAsset: PropTypes.bool,
  filledData: PropTypes.array,
  ocrDataChanged: PropTypes.bool,
  reviewTools: PropTypes.bool,
  onBulkChangePositions: PropTypes.func,
  onResetPositions: PropTypes.func,
  snapshotKey: PropTypes.string,
  lockValues: PropTypes.bool,
  positionsFieldMeta: PropTypes.array,
  currencySymbol: PropTypes.string,
  showVatAmount: PropTypes.bool,
  lockCategoryForeignCurrency: PropTypes.bool,
  lockCategoryUnbookableVat: PropTypes.bool,
  unbookableVatHint: PropTypes.string,
}

export default PositionWithTaxCategoryList
